import React, { useEffect, useState } from 'react';
import { apiUrl } from '../config/api';

interface UserRef {
  _id: string;
  username?: string;
}

interface VacationRequest {
  _id: string;
  userId?: string | UserRef; // durch populate verfügbar
  startDate: string;
  endDate: string;
  dayFraction?: number;
  vacationDays?: number;
  vacationDaysByYear?: Record<string, number>;
  status: 'pending' | 'approved' | 'declined';
  comment?: string;
}

interface VacationRequestListProps {
  requests?: VacationRequest[];
  refreshFlag: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  currentUserId: string;
  isAdmin: boolean;
}

const VacationRequestList: React.FC<VacationRequestListProps> = ({
  requests: providedRequests,
  refreshFlag,
  onEdit,
  onDelete,
  currentUserId,
  isAdmin,
}) => {
  const [requests, setRequests] = useState<VacationRequest[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editDayFraction, setEditDayFraction] = useState<'1' | '0.5'>('1');
  const [editComment, setEditComment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const fetchData = async () => {
      if (providedRequests) {
        setRequests(providedRequests);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const res = await fetch(apiUrl('/api/vacation-requests'), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          console.error("Fehler beim Laden der Urlaubsanträge:", await res.text());
          return;
        }

        const data = await res.json();
        setRequests(data);
      } catch (err) {
        console.error("Netzwerkfehler beim Laden der Urlaubsanträge:", err);
      }
    };
    fetchData();
  }, [providedRequests, refreshFlag]);

  useEffect(() => {
    setCurrentPage(1);
  }, [requests.length]);

  const sortedRequests = [...requests].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );
  const totalPages = Math.max(1, Math.ceil(requests.length / pageSize));
  const pageStartIndex = (currentPage - 1) * pageSize;
  const visibleRequests = sortedRequests.slice(pageStartIndex, pageStartIndex + pageSize);

  const goToPage = (page: number) => {
    setCurrentPage(Math.min(totalPages, Math.max(1, page)));
  };

  const paginationItems = Array.from({ length: totalPages }, (_, index) => index + 1)
    .filter(
      (page) =>
        page === 1 ||
        page === totalPages ||
        Math.abs(page - currentPage) <= 1
    )
    .reduce<Array<number | 'ellipsis'>>((items, page) => {
      const previous = items[items.length - 1];
      if (typeof previous === 'number' && page - previous > 1) {
        items.push('ellipsis');
      }
      items.push(page);
      return items;
    }, []);

  const handleEdit = (req: VacationRequest) => {
    setEditingId(req._id);
    setEditStart(req.startDate.slice(0, 10));
    setEditEnd(req.endDate.slice(0, 10));
    setEditDayFraction(req.dayFraction === 0.5 ? '0.5' : '1');
    setEditComment(req.comment || '');
  };

  const handleSave = async (id: string) => {
    const token = localStorage.getItem('token');
    await fetch(apiUrl(`/api/vacation-requests/${id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        startDate: editStart,
        endDate: editEnd,
        dayFraction: Number(editDayFraction),
        comment: editComment,
      }),
    });
    setEditingId(null);
    if (onEdit) onEdit();
  };

  const handleDelete = async (id: string) => {
    const token = localStorage.getItem('token');
    await fetch(apiUrl(`/api/vacation-requests/${id}`), {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (onDelete) onDelete();
  };

  return (
    <div>
      <h2>Urlaubsanträge</h2>
      <table className="table table-striped">
        <thead>
          <tr>
            {isAdmin && <th>Benutzer</th>}
            <th>Startdatum</th>
            <th>Enddatum</th>
            <th>Umfang</th>
            <th>Status</th>
            <th>Kommentar</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {visibleRequests.map((req) => {
            const requestUserId =
              typeof req.userId === 'string' ? req.userId : req.userId?._id;
            const username =
              typeof req.userId === 'string'
                ? 'Unbekannt'
                : req.userId?.username || 'Unbekannt';
            const isOwn = requestUserId === currentUserId;
            const canModify = isAdmin || (isOwn && req.status !== 'approved');

            return (
              <tr key={req._id}>
                {isAdmin && <td>{username}</td>}
                <td>
                  {editingId === req._id ? (
                    <input
                      type="date"
                      value={editStart}
                      onChange={(e) => setEditStart(e.target.value)}
                    />
                  ) : (
                    new Date(req.startDate).toLocaleDateString('de-DE')
                  )}
                </td>
                <td>
                  {editingId === req._id ? (
                    <input
                      type="date"
                      value={editEnd}
                      onChange={(e) => setEditEnd(e.target.value)}
                    />
                  ) : (
                    new Date(req.endDate).toLocaleDateString('de-DE')
                  )}
                </td>
                <td>
                  {editingId === req._id ? (
                    <select
                      value={editDayFraction}
                      onChange={(e) => setEditDayFraction(e.target.value as '1' | '0.5')}
                    >
                      <option value="1">Ganzer Tag</option>
                      <option value="0.5">Halber Tag</option>
                    </select>
                  ) : (
                    req.dayFraction === 0.5 ? 'Halber Tag' : 'Ganzer Tag'
                  )}
                </td>
                <td>{req.status}</td>
                <td>
                  {editingId === req._id ? (
                    <input
                      type="text"
                      value={editComment}
                      onChange={(e) => setEditComment(e.target.value)}
                    />
                  ) : (
                    req.comment || ''
                  )}
                </td>
                <td>
                  {editingId === req._id ? (
                    <>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleSave(req._id)}
                      >
                        Speichern
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setEditingId(null)}
                      >
                        Abbrechen
                      </button>
                    </>
                  ) : (
                    canModify && (
                      <>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleEdit(req)}
                        >
                          Bearbeiten
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(req._id)}
                        >
                          Löschen
                        </button>
                      </>
                    )
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {requests.length > pageSize && (
        <nav aria-label="Urlaubsanträge Seiten" className="app-pagination">
          <ul className="pagination pagination-sm app-pagination-list">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                type="button"
                onClick={() => goToPage(currentPage - 1)}
              >
                ‹
              </button>
            </li>

            {paginationItems.map((item, index) =>
              item === 'ellipsis' ? (
                <li key={`ellipsis-${index}`} className="page-item disabled">
                  <span className="page-link">…</span>
                </li>
              ) : (
                <li
                  key={item}
                  className={`page-item ${item === currentPage ? 'active' : ''}`}
                >
                  <button
                    className="page-link"
                    type="button"
                    onClick={() => goToPage(item)}
                  >
                    {item}
                  </button>
                </li>
              )
            )}

            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                type="button"
                onClick={() => goToPage(currentPage + 1)}
              >
                ›
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default VacationRequestList;
